import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import { createPayment, searchOpenCharges } from "@/api/finance";
import { fetchStudents } from "@/api/students";
import { PAYMENT_METHOD_LABELS } from "@/types/enums";
import type { PaymentMethod } from "@/types/enums";
import { formatMoney, periodLabel } from "@/utils/format";
import { extractErrorMessage } from "@/types/common";
import type { Student } from "@/types/student";

interface AcceptPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  /** Если передан — ученик уже выбран (открытие с карточки ученика), поле выбора скрывается. */
  initialStudent?: Student | null;
}

export function AcceptPaymentDialog({ open, onClose, initialStudent }: AcceptPaymentDialogProps) {
  const queryClient = useQueryClient();
  const [studentQuery, setStudentQuery] = useState("");
  const [student, setStudent] = useState<Student | null>(initialStudent ?? null);
  const [chargeId, setChargeId] = useState("");
  const [amount, setAmount] = useState<number>(0);
  const [method, setMethod] = useState<PaymentMethod>("CASH");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [comment, setComment] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setStudent(initialStudent ?? null);
  }, [open, initialStudent]);

  const { data: studentsPage } = useQuery({
    queryKey: ["students", "search", studentQuery],
    queryFn: () => fetchStudents({ search: studentQuery, limit: 20 }),
    enabled: !initialStudent && studentQuery.length > 1,
  });

  const { data: charges } = useQuery({
    queryKey: ["open-charges", student?.id],
    queryFn: () => searchOpenCharges(student!.id),
    enabled: Boolean(student),
  });

  const mutation = useMutation({
    mutationFn: () => createPayment({ studentId: student!.id, chargeId, amount, method, paidAt, comment: comment || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["payments"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["debtors"] });
      void queryClient.invalidateQueries({ queryKey: ["student-charges", student?.id] });
      setChargeId("");
      setAmount(0);
      setComment("");
      onClose();
    },
    onError: (error) => setServerError(extractErrorMessage(error)),
  });

  const selectedCharge = (charges ?? []).find((c) => c.id === chargeId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Приём оплаты</DialogTitle>
      <DialogContent>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}
        <Stack spacing={2} mt={1}>
          {!initialStudent && (
            <Autocomplete
              options={studentsPage?.items ?? []}
              getOptionLabel={(option) => `${option.fullName} — ${option.phone}`}
              value={student}
              onChange={(_, value) => {
                setStudent(value);
                setChargeId("");
              }}
              onInputChange={(_, value) => setStudentQuery(value)}
              renderInput={(params) => <TextField {...params} label="Ученик" placeholder="Начните вводить ФИО или телефон" />}
            />
          )}
          {student && (
            <TextField select label="Начисление (период)" value={chargeId} onChange={(e) => setChargeId(e.target.value)}>
              {(charges ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {periodLabel(c.period)} — остаток {formatMoney(Math.max(0, c.left ?? c.amount))}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label="Сумма"
            type="number"
            value={amount || ""}
            onChange={(e) => setAmount(Number(e.target.value))}
            helperText={selectedCharge ? `К оплате по начислению: ${formatMoney(selectedCharge.left ?? selectedCharge.amount)}` : undefined}
          />
          <TextField select label="Способ оплаты" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            {Object.entries(PAYMENT_METHOD_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Дата оплаты" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Комментарий" value={comment} onChange={(e) => setComment(e.target.value)} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button variant="contained" disabled={!student || !chargeId || !amount || mutation.isPending} onClick={() => mutation.mutate()}>
          Принять оплату
        </Button>
      </DialogActions>
    </Dialog>
  );
}
